import React, {createRef, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import moment from "moment";
import axios from "axios";
import { TablePagination, Button, useTheme } from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import { Cached, Refresh } from "@material-ui/icons";

import { useFlightJetPlans } from "../../../services/jeppesen";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const AccessControl = require("../../../AccessControl");
const apiSitePrefix = getApiSitePrefix();

const FlightJetPlanList = (_props) => {
  const {isAuthenticated, user} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const props = {
    ..._props
  }
  const classes = props.classes;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = ['FAILED'];

  const flightJetPlans = useFlightJetPlans({tableRef, queryClient, extraKeys});

  const refreshList = async () => {
    queryClient.invalidateQueries(["FlightJetPlans"]);
  }

  const retryPlan = async (rowData, method) => {
    SystemData.toggleLoadingProgress(true);
    await axios.put(`${apiSitePrefix}/flightjetplans/retry/${rowData.flightJetPlanUuid}/${method}`);
    queryClient.invalidateQueries(["FlightJetPlans"]);
    SystemData.toggleLoadingProgress(false);
  }

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: "never",
    };

    return [
      {
        ...baseColumn,
        ...ColumnWidth(100, 8),
        title: "Flight No.",
        field: "flightNo",
        render: (rowData) => {
          return (<div>{rowData.airlineCode} {rowData.flightNo}</div>);
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(160),
        title: "Flight Date",
        field: "jeppsParams",
        customFilterAndSearch: (term, rowData) => {
          const startDate = moment.utc(rowData.jeppsParams.startTimeUTC).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return startDate.indexOf(termUpper) !== -1
        },
        render: (rowData) => {
          return (<div>
            <div style={{display: 'inline-block', width: 90}}>{moment.utc(rowData.jeppsParams.startTimeUTC).format('DD MMM YYYY')}</div>
            <div style={{display: 'inline-block', width: 60}}>{moment.utc(rowData.jeppsParams.startTimeUTC).format('HH:mm')}</div>
          </div>);
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(110),
        title: "Trip",
        field: "tripNo",
      },
      {
        ...baseColumn,
        ...ColumnWidth(90),
        title: "Aircraft",
        field: "jeppsParams",
        customFilterAndSearch: (term, rowData) => (`${rowData.jeppsParams.aircraftReg}`.toUpperCase()).indexOf(`${term}`.toUpperCase()) !== -1,
        render: (rowData) => {
          return rowData.jeppsParams.aircraftReg
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Origin",
        field: "jeppsParams",
        customFilterAndSearch: (term, rowData) => (rowData.jeppsParams.originAirportCode) === `${term}`.toUpperCase(),
        render: (rowData) => {
          return rowData.jeppsParams.originAirportCode
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Destination",
        field: "jeppsParams",
        customFilterAndSearch: (term, rowData) => (rowData.jeppsParams.destinationAirportCode) === `${term}`.toUpperCase(),
        render: (rowData) => {
          return rowData.jeppsParams.destinationAirportCode
        }
      },
      {
        ...baseColumn,
        ...ColumnWidth(80),
        title: "Status",
        field: "status",
      },
      {
        ...baseColumn,
        title: "Error",
        field: "planData",
        cellStyle: { fontSize:14, padding: "8px 2px", minWidth: 600 },
        headerStyle: { padding: "8px 2px", minWidth: 500 },
        render: (rowData) => {
          if (rowData.planData && rowData.planData.error) {
            if (rowData.planData.error.translated) {
              return `(${rowData.planData.error.code}) ${rowData.planData.error.translated}`;
            }
            return `(${rowData.planData.error.code}) ${rowData.planData.error.text}`;
          } else {
            return 'Unknown Error';
          }
        } 
      },
    ];
  };
  
  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 15,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[10, 15, 30]}
                  />
              ),
            }}
            actions={[
              (rowData) => (
                {
                  icon: () => {
                    return (
                      <Button style={{height: 24}}
                        variant="contained"
                        color="primary"
                        startIcon={<Cached/>}
                        className={classes.button}
                      >
                        Retry
                      </Button>
                    )
                  },
                  tooltip: "Retry",
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    retryPlan(rowData, 'PLAN');
                  }
                }
              ),
              (rowData) => (
                AccessControl.userHasAccess(user, ["Super Admin"]) ?
                {
                  icon: () => {
                    return (
                      <Button style={{height: 24, width: 165}}
                        variant="contained"
                        color="secondary"
                        startIcon={<Cached/>}
                        className={classes.button}
                      >
                        Default Route
                      </Button>
                    )
                  },
                  tooltip: "Retry using a default Jeppesen route",
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    retryPlan(rowData, 'BASE');
                  }
                } :
                  undefined
              ),
                {
                  icon: () => {
                    return (
                      <Refresh/>
                    )
                  },
                  tooltip: "Refresh List",
                  isFreeAction: true,
                  onClick: (e) => {
                    refreshList();
                  }
                }
            ]}
            title="Flight Jet Plan Management"
            columns={getColumns()}
            data={flightJetPlans.data}
        />
      </div>
  );

}

export default FlightJetPlanList;
